import { useEffect } from "react";
import { useSettingsStore } from "../store/tenantStore";
import { getPresetById } from "../config/themePresets";

/** Injects a Google Fonts <link> for `fontName` if not already present. */
function ensureGoogleFont(fontName: string) {
  const linkId = `gfont-${fontName.replace(/\s+/g, "-").toLowerCase()}`;
  if (document.getElementById(linkId)) return;
  const link = document.createElement("link");
  link.id = linkId;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@300;400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

/** Injects theme CSS variables and the `dark` class onto <html>. */
export function applyColorPreset(presetId: string, isDark: boolean) {
  const preset = getPresetById(presetId);
  const root = document.documentElement;
  // Primary brand colour — used both by shadcn tokens and by components
  // that reference --brand-primary directly.
  root.style.setProperty("--primary", preset.primary);
  root.style.setProperty("--brand-primary", preset.primary);
  root.style.setProperty("--brand-primary-hover", preset.primaryHover ?? preset.primary);
  root.style.setProperty("--ring", preset.primary);
  root.style.setProperty("--accent", preset.accent);
  root.style.setProperty("--brand-accent", preset.accent);
  // Optional foreground (text) override per preset — ONLY in light mode.
  // In dark mode the .dark class already provides the correct light text colour.
  if (preset.foreground && !isDark) {
    root.style.setProperty("--foreground", preset.foreground);
    root.style.setProperty("--card-foreground", preset.foreground);
    root.style.setProperty("--popover-foreground", preset.foreground);
  } else {
    root.style.removeProperty("--foreground");
    root.style.removeProperty("--card-foreground");
    root.style.removeProperty("--popover-foreground");
  }
  // Optional font family override (e.g. DM Sans for 301io theme)
  if (preset.fontFamily) {
    ensureGoogleFont(preset.fontFamily);
    root.style.setProperty(
      "--theme-font-family",
      `'${preset.fontFamily}', sans-serif`
    );
  } else {
    root.style.removeProperty("--theme-font-family");
  }
  // Optional button/card border-radius override (e.g. pill shape for 301io)
  if (preset.buttonRadius) {
    root.style.setProperty("--theme-button-radius", preset.buttonRadius);
    root.style.setProperty("--radius", preset.buttonRadius === "9999px" ? "0.5rem" : preset.buttonRadius);
  } else {
    root.style.removeProperty("--theme-button-radius");
  }
  // Update data-color-theme so any CSS-selector rules stay consistent
  root.setAttribute("data-color-theme", presetId);
}

export function applyDarkMode(isDark: boolean) {
  if (isDark) {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }
}

export function applyFontSize(size: "small" | "medium" | "large") {
  const px = size === "small" ? "14px" : size === "large" ? "18px" : "16px";
  document.documentElement.style.fontSize = px;
}

/**
 * Call once near the top of the component tree (e.g. App).
 * Reactively applies every stored setting to the DOM.
 *
 * No hydration guard needed: tenantStore.readPersistedSettings() pre-seeds the
 * Zustand store with the correct values synchronously, so the store already
 * reflects the user's saved theme on the very first render. The blocking
 * <script> in index.html applies the same theme before React loads, preventing
 * any visual flash. We simply react to settings changes unconditionally.
 */
export function useThemeApplier() {
  const { settings } = useSettingsStore();

  useEffect(() => {
    // Resolve effective dark mode (light/dark/system)
    let isDark = false;
    const { theme } = settings;
    if (theme === "dark") {
      isDark = true;
    } else if (theme === "system") {
      isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    }

    // Apply dark mode FIRST so foreground logic sees the correct mode
    applyDarkMode(isDark);
    applyColorPreset(settings.colorTheme ?? "301io", isDark);
    applyFontSize(settings.fontSize ?? "medium");
  }, [settings]);

  // For "system" mode: watch OS preference changes in real time
  useEffect(() => {
    if (settings.theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      applyDarkMode(e.matches);
      applyColorPreset(settings.colorTheme ?? "301io", e.matches);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [settings.theme, settings.colorTheme]);
}

